import { Controller } from "@hotwired/stimulus";
import { debounce } from "../lib/utils";

export default class extends Controller {
  static targets = ["nav", "toggle", "menu"];

  connect() {
    this.checkOverflow();
    this.onResize = debounce(() => this.checkOverflow(), 200);
    window.addEventListener("resize", this.onResize);
  }

  disconnect() {
    window.removeEventListener("resize", this.onResize);
  }

  checkOverflow() {
    this.hideOverflowMenu();

    const containerWidth = this.navTarget.getBoundingClientRect().width;
    const toggleWidth = this.getHiddenElementWidth(this.toggleTarget);

    const navLinks = this.navTarget.querySelectorAll(".nav-link");

    // Use fractional widths throughout: integer offsetWidth values round each
    // link independently, and the accumulated rounding error can push the last
    // link into the overflow menu even when there is plenty of room.
    let totalWidth = 0;

    let overflowItems = [];

    // Calculate the total width of all nav links and identify overflow items
    navLinks.forEach((link) => {
      const style = window.getComputedStyle(link);
      const margin =
        parseFloat(style.marginLeft) + parseFloat(style.marginRight);

      // The container width must exclude the toggle button for all but the last nav link
      let totalContainerWidth = containerWidth;
      if (link !== navLinks[navLinks.length - 1]) {
        totalContainerWidth -= toggleWidth;
      }

      totalWidth += link.getBoundingClientRect().width + margin;
      if (totalWidth > totalContainerWidth) {
        overflowItems.push(link);
      }
    });

    if (overflowItems.length > 0) {
      this.showOverflowMenu(overflowItems);
    }
  }

  showOverflowMenu(overflowItems) {
    // Clear the current menu
    this.menuTarget.innerHTML = "";

    // Copy overflow nav-link items to the dropdown and hide the originals
    overflowItems.forEach((item) => {
      const clonedNode = item.cloneNode(true);
      clonedNode.classList.remove("nav-link");
      clonedNode.classList.add("dropdown-component__item");
      // The original keeps the hotkey; a second copy would fire it twice.
      clonedNode.removeAttribute("data-hotkey");
      if (clonedNode.classList.contains("active")) {
        clonedNode.setAttribute("aria-current", "page");
      }
      this.menuTarget.appendChild(clonedNode);
      item.classList.add("d-none");
    });

    // Show the toggle button if hidden
    this.toggleTarget.classList.remove("d-none");
  }

  hideOverflowMenu() {
    this.application
      .getControllerForElementAndIdentifier(
        this.menuTarget.closest('[data-controller~="dropdown-component"]'),
        "dropdown-component",
      )
      ?.hide();

    // Get all current items in the dropdown
    const menuItems = this.menuTarget.querySelectorAll(
      ".dropdown-component__item",
    );

    menuItems.forEach((item) => {
      const original = this.navTarget.querySelector(
        `[data-responsive-nav-id="${item.dataset.responsiveNavId}"]`,
      );
      if (original) {
        // Show the original nav-link item
        original.classList.remove("d-none");
      }
    });

    // Hide the overflow menu
    this.menuTarget.innerHTML = "";
    this.toggleTarget.classList.add("d-none");
  }

  getHiddenElementWidth(element) {
    const clone = element.cloneNode(true);

    // Apply styles to ensure the element is rendered but not visible
    clone.style.visibility = "hidden";
    clone.style.position = "absolute";

    // Remove Bootstrap display classes, and the attributes that would
    // duplicate the trigger's id and popover target
    clone.classList.remove("d-none");
    clone.removeAttribute("id");
    clone.removeAttribute("popovertarget");

    document.body.appendChild(clone);

    const width = clone.getBoundingClientRect().width;

    document.body.removeChild(clone);

    return width;
  }
}
